/* eslint-disable react/no-unsafe */
/**
 * Textarea with all the trimmings on video and article pages beneath the content
 * that allow users to submit different discussion types.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {findDOMNode} from "react-dom";
import {connect} from "react-redux";

import {Id, View} from "@khanacademy/wonder-blocks-core";
import {Checkbox, TextArea} from "@khanacademy/wonder-blocks-form";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {border, color} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import Log, {Errors} from "@khan/logging";
import {standardTransition} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedLink from "#components/link-package/deprecated-link.ts";
import {domainColors} from "#content-library-legacy/styles/shared.ts";
import type {ApolloGqlClient} from "#data-access/gql/apollo-types.ts";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import {
    getZendeskCommunityGuidelinesLink,
    getZendeskCommunityLink,
} from "#zendesk/zendesk-util.ts";

import getFeedbackEditTip from "../feedback_edit_tip.ts";
import type {AddFeedbackToDiscussion_addFeedbackToDiscussion_lowQualityResponse as AddFbLqResp} from "../graphql/__graphql-types__/AddFeedbackToDiscussion.ts";
import type {UpdateFeedback_updateFeedback_lowQualityResponse as UpdateFbLqResp} from "../graphql/__graphql-types__/UpdateFeedback.ts";
import {ADD_FEEDBACK_TO_DISCUSSION} from "../graphql/add_feedback_to_discussion.ts";
import {UPDATE_FEEDBACK} from "../graphql/update_feedback.ts";
import * as sharedDiscussionStyles from "../shared-styles.ts";
import {getTabsStrings} from "../strings.ts";
import type {FeedbackType, LowQualityNotice, ServerPost} from "../types.ts";
import {userHasJustLoggedIn} from "../util.tsx";
import {filterFeedback} from "../util/feedback-filters.ts";
import {toGqlFeedbackType} from "../util/feedback-type-converters.ts";

import {
    editPost,
    resetFocusOnTopLevelInputAction,
} from "./discussion-actions.ts";
import DiscussionAvatar from "./discussion-avatar.tsx";
import DiscussionInputFooter from "./discussion-input-footer.tsx";
import type {State as ReducerState} from "./discussion-reducer.ts";

type FeedbackToDiscussionType =
    | "ALL_PROJECT_ANSWER"
    | "ALL_PROJECT_QUESTION"
    | "ANSWER"
    | "COMMENT"
    | "PROGRAM"
    | "PROJECTFEEDBACK"
    | "PROJECT_CRITIQUE_ANSWER"
    | "PROJECT_CRITIQUE_QUESTION"
    | "PROJECT_EVAL_ANSWER"
    | "PROJECT_EVAL_QUESTION"
    | "PROJECT_HELP_ANSWER"
    | "PROJECT_HELP_QUESTION"
    | "QUESTION"
    | "REPLY"
    | "SUGGESTED_CLARIFICATION";

const defaultDomainColor = domainColors("default").domain1;

const HelpCenterLink = () => {
    return (
        <a href={getZendeskCommunityLink()} className={css(styles.helpLink)}>
            {t`Go to our Help Center for technical issues or feature requests`}
        </a>
    );
};

const CommunityGuidelinesLink = () => {
    return (
        <a
            href={getZendeskCommunityGuidelinesLink()}
            className={css(styles.guidelinesLink)}
        >
            {t`Read our Community Guidelines before joining the conversation`}
        </a>
    );
};

type DefaultProps = {
    initialPostContent: string;
    // If the user is currently editing a post (as opposed to composing a new one).
    editing: boolean;
    // Whether to show the poster's avatar next to the input.
    showAvatar: boolean;
};

type OwnProps = DefaultProps & {
    contentId?: string;
    contentKind?: string;
    topicSlug: string;
    tab: FeedbackType;
    // Whether this input field is for submitting an answer.
    isAnswer?: boolean;
    // Whether this input field is for submitting a reply.
    isReply?: boolean;
    // Whether we should automatically focus on the textarea after receiving a `true` value
    // for this prop.
    autoFocus?: boolean;
    // If this textarea is for an answer or a reply, we need to know the key of the post that
    // this answer or reply is responding to.
    feedbackKey?: string;
    // The container for answers needs to know whether the formattingOptionsContainer
    // is expanded in order to transition its height accordingly.
    onToggleFormattingOptionsExpanded?: (arg1: boolean) => void;
    // The container for the markdown previewer needs to know whether the previewContainer
    // is expanded in order to transition its height accordingly.
    onTogglePreviewExpanded?: (arg1: boolean) => void;
    // If clarifications have been disabled and therefore we don't want the clarifications
    // tab to ever be accessible.
    clarificationsEnabled?: boolean;
    // If the user is currently editing a clarification in order to accept it.
    acceptingClarification: boolean;
    // If the user is currently editing a clarification that has already been accepted.
    editingAcceptedClarification?: boolean;
    // Function to be called on submit if the user is created or editing an accepted clarification.
    onSubmitAcceptedClarification?: (postContent: string) => void;
    onCancel?: () => void;
    postKey?: string | null | undefined;
    resetNewPostKeyInfo?: () => void;
    onPostSuccess: (post: ServerPost, tab: FeedbackType) => void;
    onReportMistake?: () => void;
};

type ReduxProps = {
    // The youtubeId for the video if this input is tied to a video.
    youtubeId: string | null | undefined;
    // The key of the most recently submitted post that has not been rendered yet
    newPostKey?: string | null | undefined;
    onEditPost: (post: ServerPost, content: string) => void;
    resetFocusOnTopLevelInput: () => void;
};

type Props = OwnProps & ReduxProps & WithApolloProps & WithUserProfileProps;

type State = {
    textareaExpanded: boolean;
    formattingOptionsExpanded: boolean;
    previewExpanded: boolean;
    postContent: string | null | undefined;
    submitButtonDisabled: boolean;
    submitStatus: "ready" | "submitting" | "failed";
    isLowQuality: boolean;
    feedbackEditTip: string;
    isFromAuthor: boolean;
    value: string;
};

// string helper
// are responses called "answers" or "replies"?
const areResponsesCalledAnswers = (check: string): boolean => {
    return (
        check === "projecthelp" || check === "question" || check === "questions"
    );
};

class DiscussionInput extends React.Component<Props, State> {
    container: Element | null | undefined;
    textarea: HTMLTextAreaElement | null | undefined;

    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;

    static defaultProps: DefaultProps = {
        initialPostContent: "",
        editing: false,
        showAvatar: true,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            textareaExpanded: false,
            formattingOptionsExpanded: false,
            previewExpanded: false,
            postContent: props.initialPostContent,
            submitButtonDisabled: true,
            submitStatus: "ready",
            isLowQuality: false,
            feedbackEditTip: "",
            isFromAuthor: false,
            value: props.initialPostContent,
        };
    }

    componentDidMount() {
        const {isAnswer, isReply} = this.props;
        this._isMounted = true;

        // If a user logs in through the login upsell button we have in place of the
        // textarea, they presumably want to write a post. Loggin in refreshes
        // the page, so we check for this on mount instead of using a router to
        // listen for a route change.
        const {textarea} = this;
        if (
            textarea &&
            !isAnswer &&
            !isReply &&
            // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
            // @ts-expect-error - TS2345 - Argument of type 'Location' is not assignable to parameter of type '{ [href: string]: string; }'.
            // eslint-disable-next-line no-restricted-syntax
            userHasJustLoggedIn(window.location)
        ) {
            textarea.focus({preventScroll: true});
        }

        if (textarea && this.props.autoFocus) {
            textarea.focus();
        }

        document.addEventListener("mousedown", this.handleDocumentInteraction);
        document.addEventListener("touchstart", this.handleDocumentInteraction);
    }

    UNSAFE_componentWillUpdate(nextProps: Props, nextState: State) {
        if (nextProps.newPostKey && this.state.submitStatus === "submitting") {
            this.setState({
                value: "",
            });

            this.collapseTextarea();

            this.setState({
                submitStatus: "ready",
                submitButtonDisabled: true,
                isLowQuality: false,
            });

            if (this.props.resetNewPostKeyInfo) {
                this.props.resetNewPostKeyInfo();
            }
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {textarea} = this;

        if (textarea && this.props.autoFocus && !prevProps.autoFocus) {
            // HACK(diedra): setTimeout with 0 ms just makes sure that the logic
            // inside the function body is run after any other logic that is happening
            // at the same time, which in this case makes sure that the input field
            // is ready to be focused on.
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => textarea && textarea.focus(), 0);
            this.props.resetFocusOnTopLevelInput();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;

        document.removeEventListener(
            "mousedown",
            this.handleDocumentInteraction,
        );
        document.removeEventListener(
            "touchstart",
            this.handleDocumentInteraction,
        );
    }

    editingOrAcceptingClarification: () => boolean = () => {
        return !!(
            this.props.editing ||
            this.props.acceptingClarification ||
            this.props.editingAcceptedClarification
        );
    };

    handleFocus: () => void = () => {
        this.setState({textareaExpanded: true});
    };

    collapseTextarea: () => void = () => {
        if (!this._isMounted) {
            return;
        }

        this.setState({
            textareaExpanded: false,
            formattingOptionsExpanded: false,
            previewExpanded: false,
        });

        const {onToggleFormattingOptionsExpanded, onTogglePreviewExpanded} =
            this.props;
        if (onToggleFormattingOptionsExpanded) {
            onToggleFormattingOptionsExpanded(false);
        }
        if (onTogglePreviewExpanded) {
            onTogglePreviewExpanded(false);
        }
    };

    handleDocumentInteraction: (event: Event) => void = (event) => {
        if (!this.container) {
            return;
        }
        const target: Node = event.target as any;

        // If the user clicks anywhere outside of the textarea container, we collapse it.
        // TODO(diedra): We should also collapse the textarea if the user tabs away.
        // TODO(diedra): We should really check if the user clicks outside of the
        // TabbedPanel so they can switch tabs without collapsing the textarea.
        if (!this.container.contains(target)) {
            this.collapseTextarea();
        }
    };

    // TODO(diedra): Improve this heuristic to be closer to the quality check
    // we perform on the server.
    getSanitizedText: (
        text?: string | null | undefined,
    ) => string | null | undefined = (text) => {
        if (text && text.length) {
            const trimmedText = text.trim();
            if (trimmedText.length) {
                return trimmedText;
            }
        }
        return null;
    };

    handleTextChange = (value: string) => {
        const sanitizedText = this.getSanitizedText(value);
        // Submit button should be disabled if sanitizedText is falsy
        const disabled = !sanitizedText;

        this.setState({
            postContent: sanitizedText,
            value,
            submitButtonDisabled: disabled,
        });
    };

    handleKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        this.handleTextChange(event.currentTarget.value);
    };

    /**
     * Handle updating the UI after the post submission server call has completed.
     */
    updateUI: (data: {
        filteredFeedback: ServerPost | null | undefined;
        notice: LowQualityNotice | null | undefined;
    }) => void = (data: {
        filteredFeedback: ServerPost | null | undefined;
        notice: LowQualityNotice | null | undefined;
    }) => {
        const {filteredFeedback: feedback, notice: lowQualityResp} = data;
        let submitButtonDisabled = false;
        if (lowQualityResp && lowQualityResp.showLowQualityNotice) {
            const translatedFeedbackEditTip = getFeedbackEditTip(
                lowQualityResp.feedbackCode,
                lowQualityResp.feedbackType,
                lowQualityResp.feedbackChar,
            );
            if (this._isMounted) {
                this.setState({
                    submitStatus: "failed",
                    isLowQuality: true,
                    feedbackEditTip: translatedFeedbackEditTip || "",
                });
            }

            return;
        }

        const {onPostSuccess, tab, editing, onEditPost} = this.props;

        if (!feedback) {
            return;
        }

        if (editing) {
            // Updates the post in the redux store with the new content
            onEditPost(feedback, feedback.content);
        } else {
            this.setState({
                value: "",
            });
        }

        // Exits out of editing mode or handles posting a new question,
        // answer or comment.
        // TOOD(kevinb): move editing into redux store and have so that the
        // "editPost" action can exit out of editing mode
        onPostSuccess(feedback, tab);

        // Now that the textarea is empty, we need to reset the
        // submitButton to be disabled.
        submitButtonDisabled = true;
        this.collapseTextarea();

        if (this._isMounted) {
            this.setState({
                postContent: "",
                submitStatus: "ready",
                submitButtonDisabled: submitButtonDisabled,
                isLowQuality: false,
            });
        }
    };

    getPlaceholderText: () => string = () => {
        const {isReply, isAnswer, tab} = this.props;

        // are responses called "answers" or "replies"?
        const responsesAreAnswers = areResponsesCalledAnswers(tab);

        if (isReply) {
            return t`Post a comment...`;
        }
        const tabsStrings = getTabsStrings();
        if (!isAnswer && tab && tabsStrings[tab]) {
            return tabsStrings[tab].placeholder;
        }
        if (isAnswer && responsesAreAnswers) {
            return t`Post an answer...`;
        }
        return t`Post a reply...`;
    };

    getTextareaLabel: () => string = () => {
        const {isReply, isAnswer, tab, acceptingClarification} = this.props;

        // are responses called "answers" or "replies"?
        const responsesAreAnswers = areResponsesCalledAnswers(tab);

        if (isReply) {
            return t`Comment`;
        }
        if (isAnswer && responsesAreAnswers) {
            return t`Answer`;
        }
        if (isAnswer) {
            return t`Reply`;
        }
        const tabsStrings = getTabsStrings();
        if (tab && tabsStrings[tab]) {
            return tabsStrings[tab].textAreaLabel;
        }
        if (acceptingClarification) {
            return t`Accepted clarification`;
        }
        return "";
    };

    getIsModerator(): boolean {
        const {profile} = this.props;
        return !!profile && profile.get("isModerator");
    }

    updateFeedback: (
        feedbackKey: string,
        textContent: string,
        shownLowQualityNotice: boolean,
        gqlClient: ApolloGqlClient,
    ) => void = (
        feedbackKey: string,
        textContent: string,
        shownLowQualityNotice: boolean,
        gqlClient: ApolloGqlClient,
    ) => {
        gqlClient
            .mutate(UPDATE_FEEDBACK, {
                variables: {
                    feedbackKey,
                    textContent,
                    shownLowQualityNotice,
                },
            })
            .then((response) => {
                if (response?.data?.updateFeedback?.error?.code) {
                    this.handleErrors(
                        response?.data?.updateFeedback?.error?.code as string,
                    );
                } else {
                    this.updateUI(
                        processResponse(response?.data?.updateFeedback),
                    );
                }
            });
    };

    addFeedbackToDiscussion: (
        focusKind: string,
        focusId: string,
        feedbackType: FeedbackType,
        text: string,
        shownLowQualityNotice: boolean,
    ) => void = (
        focusKind: string,
        focusId: string,
        feedbackType: FeedbackType,
        text: string,
        shownLowQualityNotice: boolean,
    ) => {
        const gqlType = toGqlFeedbackType(feedbackType);
        const enumType = (gqlType as FeedbackToDiscussionType) ?? "QUESTION";
        this.props.gqlClient
            .mutate(ADD_FEEDBACK_TO_DISCUSSION, {
                variables: {
                    focusKind,
                    focusId,
                    feedbackType: enumType,
                    textContent: text,
                    shownLowQualityNotice,
                },
            })
            .then((response) => {
                if (response?.data?.addFeedbackToDiscussion?.error?.code) {
                    this.handleErrors(
                        response?.data?.addFeedbackToDiscussion?.error
                            ?.code as string,
                    );
                } else {
                    this.updateUI(
                        processResponse(
                            response?.data?.addFeedbackToDiscussion,
                        ),
                    );
                }
            });
    };

    addAnswerToQuestion: (
        parentKey: string,
        textContent: string,
        fromVideoAuthor: boolean,
        shownLowQualityNotice: boolean,
    ) => void = (
        parentKey: string,
        textContent: string,
        fromVideoAuthor: boolean,
        shownLowQualityNotice: boolean,
    ) =>
        this.addChildPost(
            parentKey,
            textContent,
            fromVideoAuthor,
            shownLowQualityNotice,
            "ANSWER",
        );

    addReply: (
        parentKey: string,
        textContent: string,
        fromVideoAuthor: boolean,
        shownLowQualityNotice: boolean,
    ) => void = (
        parentKey: string,
        textContent: string,
        fromVideoAuthor: boolean,
        shownLowQualityNotice: boolean,
    ) =>
        this.addChildPost(
            parentKey,
            textContent,
            fromVideoAuthor,
            shownLowQualityNotice,
            "REPLY",
        );

    addChildPost: (
        parentKey: string,
        textContent: string,
        fromVideoAuthor: boolean,
        shownLowQualityNotice: boolean,
        feedbackType: FeedbackToDiscussionType,
    ) => void = (
        parentKey: string,
        textContent: string,
        fromVideoAuthor: boolean | null | undefined,
        shownLowQualityNotice: boolean,
        feedbackType: FeedbackToDiscussionType,
    ) => {
        if (feedbackType !== "ANSWER" && feedbackType !== "REPLY") {
            Log.error(
                "Feedback type must be ANSWER or REPLY",
                Errors.Internal,
                {
                    sentryData: {
                        contexts: {
                            extras: {
                                feedbackType: feedbackType as string,
                            },
                        },
                    },
                },
            );
        }
        this.props.gqlClient
            .mutate(ADD_FEEDBACK_TO_DISCUSSION, {
                variables: {
                    parentKey,
                    textContent,
                    fromVideoAuthor,
                    shownLowQualityNotice,
                    feedbackType,
                },
            })
            .then((response) => {
                if (response?.data?.addFeedbackToDiscussion?.error?.code) {
                    this.handleErrors(
                        response.data.addFeedbackToDiscussion.error
                            .code as string,
                    );
                } else {
                    const data = processResponse(
                        response?.data?.addFeedbackToDiscussion,
                    );
                    if (data?.filteredFeedback && feedbackType === "ANSWER") {
                        data.filteredFeedback.questionKey = parentKey;
                    }
                    this.updateUI(data);
                }
            });
    };

    // Handle any GQL errors returned by queries
    handleErrors: (error?: string) => void = (error?: string) => {
        if (this._isMounted) {
            this.setState({submitStatus: "failed"});
            // leaving this open to expansion for handling other error types,
            // but we don't have translated strings for them yet
            switch (error) {
                case "NOT_ALLOWED":
                    this.setState({
                        feedbackEditTip: t`This discussion is locked`,
                    });
                    break;
            }
        }
    };

    getSubmissionType: () => string = () => {
        const {feedbackKey, tab, topicSlug, isAnswer, isReply, postKey} =
            this.props;

        const contentKind = this.props.contentKind
            ? this.props.contentKind.toLowerCase()
            : null;
        const contentId = this.props.contentId;

        // Update Feedback
        if (postKey) {
            return "UpdateFeedback";
        }

        // Adding a new answer
        if (!postKey && isAnswer && feedbackKey) {
            return "AddAnswer";
        }

        // Adding a new reply
        if (!postKey && isReply && feedbackKey) {
            return "AddReply";
        }

        // Adding a new Comment
        if (
            !postKey &&
            contentId &&
            contentKind &&
            tab === "comments" &&
            !isAnswer
        ) {
            return "AddComment";

            // Add a new message of all other feedback types
        }
        if (!postKey && contentId && contentKind && tab) {
            return "AddFeedback";
        }

        Log.error(
            "We don't have enough information to submit this type of post.",
            Errors.Internal,
            {
                sentryData: {
                    contexts: {
                        extras: {
                            feedbackType: tab,
                            contentKind: contentKind,
                            topicSlug: topicSlug,
                        },
                    },
                },
            },
        );
        return "error";
    };
    /**
     * Handle submitting a question to the server, either to add a new question
     * or to update an existing question.
     */
    // TODO(diedra): Move to action creators.
    handleSubmit: () => void = () => {
        const {
            feedbackKey,
            tab,
            postKey,
            acceptingClarification,
            editingAcceptedClarification,
            onSubmitAcceptedClarification,
            gqlClient,
        } = this.props;
        const {postContent, isFromAuthor, submitStatus, isLowQuality} =
            this.state;
        // It shouldn't be possible to submit a post via the UI if it's already
        // submitting or if the postContent is empty, but it doesn't hurt to make
        // sure.
        if (!postContent || submitStatus === "submitting") {
            return;
        }

        if (
            (acceptingClarification || editingAcceptedClarification) &&
            onSubmitAcceptedClarification &&
            postContent
        ) {
            onSubmitAcceptedClarification(postContent);
            return;
        }

        this.setState({
            submitStatus: "submitting",
            feedbackEditTip: "",
        });

        const contentKind = this.props.contentKind
            ? this.props.contentKind.toLowerCase()
            : null;
        const contentId = this.props.contentId;

        const submissionType = this.getSubmissionType();

        switch (submissionType) {
            case "UpdateFeedback":
                if (postKey) {
                    this.updateFeedback(
                        postKey,
                        postContent,
                        isLowQuality,
                        gqlClient,
                    );
                }
                break;
            case "AddAnswer":
                if (feedbackKey) {
                    this.addAnswerToQuestion(
                        feedbackKey,
                        postContent,
                        isFromAuthor,
                        isLowQuality,
                    );
                }
                break;
            case "AddReply":
                if (feedbackKey) {
                    this.addReply(
                        feedbackKey,
                        postContent,
                        isFromAuthor,
                        isLowQuality,
                    );
                }
                break;
            case "AddComment":
            case "AddFeedback":
            default:
                if (contentKind && contentId) {
                    this.addFeedbackToDiscussion(
                        contentKind,
                        contentId,
                        tab,
                        postContent,
                        isLowQuality,
                    );
                }
                break;
        }
    };

    renderTextarea: (id: string, showFooter: boolean) => React.ReactNode = (
        id: string,
        showFooter: boolean,
    ) => {
        const {isAnswer, isReply, editing} = this.props;
        const {textareaExpanded, submitStatus, feedbackEditTip} = this.state;

        const postIsBeingSubmitted = submitStatus === "submitting";

        const placeholderText = this.getPlaceholderText();

        const showError = !!(textareaExpanded && feedbackEditTip);
        const showNewQuestionTextarea =
            textareaExpanded && !isAnswer && !isReply && !editing;
        // If the footer is shown, there should be a flat bottom border since
        // the footer has a border that is visually attached to the textarea.
        // If the discussion input is being used for a reply, the footer does
        // not have a border so the textarea doesn't need a flat bottom border.
        const showFlatBottomBorder = showFooter && !isReply;

        const containerStyles = [
            styles.textareaContainer,
            showError && styles.textareaContainerError,
            showFlatBottomBorder && styles.flatBottomBorder,
        ];
        const textareaClassNames = [
            styles.textareaField,
            // Use the answer field size for editing or accepting clarifications.
            (isAnswer || this.editingOrAcceptingClarification()) &&
                styles.textareaFieldForAnswer,
            showNewQuestionTextarea && styles.textareaFieldExpanded,
            // Set flat bottom on textarea so it lines up with the container
            showFlatBottomBorder && styles.flatBottomBorder,
        ];

        // Reply field should initially be one row tall.
        const rows = isReply
            ? {
                  rows: 1,
              }
            : null;

        return (
            <View style={containerStyles}>
                <TextArea
                    id={id}
                    value={this.state.value}
                    style={textareaClassNames}
                    placeholder={`${placeholderText}`}
                    aria-label={placeholderText}
                    onFocus={this.handleFocus}
                    onChange={this.handleTextChange}
                    onKeyUp={this.handleKeyUp}
                    ref={(node) => (this.textarea = node)}
                    disabled={postIsBeingSubmitted}
                    resizeType="vertical"
                    // We use the validate prop to trigger the textarea error state.
                    validate={() => {
                        if (showError) {
                            return feedbackEditTip;
                        }
                    }}
                    // Since the validate prop is only called on mount (if the
                    // textarea value is not empty) or when the value changes, we
                    // need to re-trigger calling the validate function after the
                    // input is submitted. We do this by changing the key based
                    // on if there is an error. If showError changes, the textarea
                    // will re-mount and validate accordingly.
                    // TODO(WB-1748): This can be removed once WB TextArea supports
                    // validation after a form is submitted
                    key={showError.toString()}
                    {...rows}
                />
            </View>
        );
    };

    renderFooter: () => React.ReactNode = () => {
        const {
            tab,
            isAnswer,
            isReply,
            editing,
            acceptingClarification,
            editingAcceptedClarification,
            onToggleFormattingOptionsExpanded,
            onTogglePreviewExpanded,
            onCancel,
        } = this.props;
        const {
            formattingOptionsExpanded,
            previewExpanded,
            submitButtonDisabled,
            submitStatus,
            textareaExpanded,
        } = this.state;

        const moderatingAcceptedClarification =
            acceptingClarification || editingAcceptedClarification;

        const canShowFormattingOptions =
            !!(textareaExpanded || isAnswer || editing) &&
            !acceptingClarification;

        return (
            <DiscussionInputFooter
                tab={tab}
                content={this.state.postContent || ""}
                isAnswer={isAnswer}
                isReply={isReply}
                editing={editing}
                acceptingClarification={moderatingAcceptedClarification}
                submitButtonDisabled={
                    submitButtonDisabled && !moderatingAcceptedClarification
                }
                submitStatus={submitStatus}
                onSubmit={this.handleSubmit}
                onCancel={onCancel}
                canShowFormattingOptions={canShowFormattingOptions}
                formattingOptionsExpanded={formattingOptionsExpanded}
                onToggleFormattingOptionsExpanded={(newExpandedState) => {
                    if (onToggleFormattingOptionsExpanded) {
                        onToggleFormattingOptionsExpanded(newExpandedState);
                    }
                    this.setState({
                        formattingOptionsExpanded: newExpandedState,
                    });
                }}
                previewExpanded={previewExpanded}
                onTogglePreviewExpanded={(newExpandedState) => {
                    if (onTogglePreviewExpanded) {
                        onTogglePreviewExpanded(newExpandedState);
                    }
                    this.setState({
                        previewExpanded: newExpandedState,
                    });
                }}
            />
        );
    };

    render(): React.ReactNode {
        const {
            tab,
            isAnswer,
            isReply,
            showAvatar,
            clarificationsEnabled,
            contentKind,
        } = this.props;
        const {textareaExpanded, feedbackEditTip, isFromAuthor} = this.state;

        const textAreaLabel = this.getTextareaLabel();

        const tabStringsForTab = (tab && getTabsStrings()[tab]) || {};
        // Tips don't appear on answer fields.
        // @ts-expect-error - TS2339 - Property 'tipLabel' does not exist on type '{}'.
        const tipLabel = !isAnswer && tabStringsForTab.tipLabel;
        // @ts-expect-error - TS2339 - Property 'tipContents' does not exist on type '{}'.
        const tipContents = !isAnswer && tabStringsForTab.tipContents;

        const showError = textareaExpanded && feedbackEditTip;
        const showFooter =
            textareaExpanded ||
            isAnswer ||
            this.editingOrAcceptingClarification();
        const showNewQuestionTextarea =
            textareaExpanded &&
            !isAnswer &&
            !isReply &&
            !this.editingOrAcceptingClarification();

        const showReportMistakeLink =
            tab !== "clarifications" && clarificationsEnabled;

        const formContents = (idRoot: string) => {
            const contentInputId = `${idRoot}content-input`;

            return (
                <View
                    style={isReply ? styles.replyContainer : styles.container}
                    ref={(node) =>
                        (this.container = findDOMNode(node) as Element)
                    }
                >
                    {/* It's important to always render the aria-live so the SR
                    can register it and listen for changes*/}
                    <View aria-live="polite" style={styles.errorText}>
                        {showError && <Body>{feedbackEditTip}</Body>}
                    </View>
                    <View
                        style={[
                            styles.textAreaHeading,
                            showAvatar && styles.textAreaHeadingMargin,
                        ]}
                    >
                        <label htmlFor={contentInputId}>
                            <Body>{textAreaLabel}</Body>
                        </label>
                        <Spring />
                        {isAnswer &&
                        contentKind !== "scratchpad" &&
                        this.getIsModerator() ? (
                            <Checkbox
                                label={t`From the author:`}
                                style={styles.postAsAuthorCheckbox}
                                checked={isFromAuthor}
                                onChange={(checked) =>
                                    this.setState({isFromAuthor: checked})
                                }
                            />
                        ) : null}
                    </View>
                    <View style={styles.expandableContainer}>
                        {showAvatar && (
                            <View style={styles.avatarContainer}>
                                <DiscussionAvatar
                                    showCurrentUserAvatar={true}
                                />
                            </View>
                        )}

                        <View style={styles.textareaAndTipsContainer}>
                            {this.renderTextarea(contentInputId, showFooter)}

                            {showFooter && this.renderFooter()}

                            {showNewQuestionTextarea && (
                                <View>
                                    <CommunityGuidelinesLink />
                                    <View style={styles.tip}>
                                        <View style={styles.tipHeading}>
                                            <LabelLarge>{tipLabel}</LabelLarge>
                                        </View>

                                        {tipContents}
                                    </View>

                                    <View style={styles.helpLinksContainer}>
                                        <HelpCenterLink />
                                        {showReportMistakeLink && (
                                            <DeprecatedLink
                                                className={css(styles.helpLink)}
                                                onClick={
                                                    this.props.onReportMistake
                                                }
                                            >
                                                {t`Report a mistake`}
                                            </DeprecatedLink>
                                        )}
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>
                </View>
            );
        };

        return <Id>{(idRoot) => <form>{formContents(idRoot)}</form>}</Id>;
    }
}

const mapLowQualityNotice = (
    lqResp?: AddFbLqResp | UpdateFbLqResp | null,
): LowQualityNotice | null | undefined => {
    if (!lqResp) {
        return null;
    }
    const {feedbackChar, feedbackCode, feedbackType, showLowQualityNotice} =
        lqResp;
    return {
        feedbackChar,
        feedbackCode,
        feedbackType: feedbackType as string,
        showLowQualityNotice,
    };
};

const processResponse = (
    data?: {
        // Making these "any" because TypeScript is annoying about completely identical fields in different types.
        // Once we're on TypeScript, we should be able to make this more precise.
        feedback: any | null | undefined;
        lowQualityResponse: any | null | undefined;
    } | null,
): {
    filteredFeedback: ServerPost | null | undefined;
    notice: LowQualityNotice | null | undefined;
} => {
    let filteredFeedback;
    let notice;
    if (!data) {
        return {filteredFeedback, notice};
    }
    if (data?.feedback) {
        filteredFeedback = filterFeedback(data?.feedback);
    }
    if (data?.lowQualityResponse) {
        notice = mapLowQualityNotice(data?.lowQualityResponse);
    }
    return {filteredFeedback, notice};
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: color.white,
    },

    replyContainer: {
        backgroundColor: color.offWhite,
    },

    errorText: {
        marginLeft: 64,
        color: color.red,
    },

    expandableContainer: {
        flexDirection: "row",
        gap: sharedDiscussionStyles.avatarFormSpace,
    },

    avatarContainer: {
        minWidth: "auto", // This prevents avatar from overflowing the container
    },

    textareaAndTipsContainer: {
        width: "100%",
    },

    textAreaHeading: {
        flexDirection: "row",
    },

    textAreaHeadingMargin: {
        marginLeft: 56,
    },

    postAsAuthorCheckbox: {
        // TODO(briang or kevinb): Remove this minWidth when the fix comes in
        // the WonderBlocks Checkbox to avoid it being compressed.
        minWidth: 148,
        marginTop: 4,
    },

    textareaContainer: {
        // We have to explicitly specify the top margin here to center this textarea
        // vertically *and* have it stay in that position when it expands.
        // If not for the expanding logic, we could use flexbox to make the centering
        // more stable, but alas. Life.
        marginTop: 4,
        marginBottom: 0,
        borderRadius: 4,
        flex: 1,
        border: sharedDiscussionStyles.border,
        backgroundColor: color.white,
    },

    flatBottomBorder: {
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
    },

    textareaContainerError: {
        borderColor: color.red,
        backgroundColor: sharedDiscussionStyles.errorBackgroundColor,
    },

    textareaField: {
        // Hide border color from TextArea since container includes the border
        borderColor: "transparent",
        transition: `min-height ${standardTransition}`,
        minHeight: 34,
        overflow: "auto",
        ":focus-visible": {
            outlineOffset: `calc(${border.width.thin} * -1)`, // Adjust outline offset so it covers the container's border
        },
    },

    textareaFieldForAnswer: {
        minHeight: 62,
    },

    textareaFieldExpanded: {
        minHeight: sharedDiscussionStyles.textareaMinHeight,
        overflow: "auto",
    },

    tip: {
        marginTop: 24,
        marginBottom: 16,
        padding: 24,
        borderRadius: 2,
        backgroundColor: sharedDiscussionStyles.secondaryBackgroundColor,
    },

    tipHeading: {
        marginBottom: 8,
    },

    helpLinksContainer: {
        textAlign: "right",
    },

    helpLink: {
        color: defaultDomainColor,
        textDecoration: "none",

        ":hover": {
            textDecoration: "underline",
        },
    },

    guidelinesLink: {
        marginTop: 16,
        color: defaultDomainColor,
        textDecoration: "none",

        ":hover": {
            textDecoration: "underline",
        },
        textAlign: "right",
    },
});

const mapStateToProps = (state: ReducerState) => {
    return {
        newPostKey: state.newPostKey,
        youtubeId: state.youtubeId,
    };
};

const mapDispatchToProps = {
    onEditPost: editPost,
    resetFocusOnTopLevelInput: resetFocusOnTopLevelInputAction,
} as const;

type ExportProps = WithoutUserProfile<
    WithoutApollo<
        Without<
            JSX.LibraryManagedAttributes<
                typeof DiscussionInput,
                React.ComponentProps<typeof DiscussionInput>
            >,
            ReduxProps
        >
    >
>;

export default compose(
    withUserProfile,
    withApollo,
    connect(mapStateToProps, mapDispatchToProps),
)(DiscussionInput) as React.ComponentType<ExportProps>;
